import React from "react";
import { Box, Button, Typography } from "@mui/material";

import { useNavigateWithClient } from "hooks";

import InterviewInstructionsModal from "../../components/InterviewInstructionsModal";
import InterviewCountdown from "../../components/InterviewCountdown";

/**
 * "Track your attempts performance" (mobile) — mobile equivalent of desktop's
 * `UnlockCard` (PerformanceReview/GraphAnalysis).
 *
 * The promo/CTA that nudges another attempt. "Practice again" reuses the mobile
 * interview flow: instructions modal → countdown → /assessment?tid, the same
 * path the dashboard cards take. `disabled` mirrors desktop's guest gate.
 *
 * @param {object} template   the feedback template (from the store)
 * @param {boolean} [disabled]
 */
export default function TrackAttempts({ template, disabled = false }) {
  const navigateWithClient = useNavigateWithClient();
  const [showInstructions, setShowInstructions] = React.useState(false);
  const [countdown, setCountdown] = React.useState(false);

  const handleStart = () => {
    setShowInstructions(false);
    setCountdown(true);
  };

  const handleComplete = () => {
    setCountdown(false);
    if (template?._id) navigateWithClient(`/assessment?tid=${template._id}`);
  };

  return (
    <Box
      sx={{
        bgcolor: "shades.clr-white-900",
        border: "1px solid",
        borderColor: "neutral.clr-100",
        borderRadius: 3,
        p: 5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 3,
      }}
    >
      <Typography variant="h6-semiBold" color="shades.clr-black-900">
        Track your attempts performance
      </Typography>

      <Box
        component="img"
        src="https://assets.languify.in/images/performace-graph.png"
        alt="Attempts performance"
        sx={{ height: 120, objectFit: "contain" }}
      />

      <Typography
        variant="overline-bold"
        color="primary.clr-300"
        sx={{ textTransform: "uppercase" }}
      >
        Each attempt brings you closer to your goal!
      </Typography>

      <Typography variant="body01-semiBold" color="success.clr-700">
        Take one more attempt to boost your score in speech and content
      </Typography>

      <Button
        fullWidth
        variant="outlined"
        onClick={() => setShowInstructions(true)}
        disabled={disabled || !template?._id}
      >
        Practice again
      </Button>

      <InterviewInstructionsModal
        open={showInstructions}
        template={template}
        onClose={() => setShowInstructions(false)}
        onStart={handleStart}
      />
      <InterviewCountdown open={countdown} onComplete={handleComplete} />
    </Box>
  );
}
